import { workspaceRxapi as rxapi, workspaceApi as api } from '../api';
import type {
    WorkspaceRecord,
    WorkspaceBundle,
    WorkspaceConfig,
    Workspace,
    Widget,
} from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import type { ResourceQuery, ResourceQueryResponseWithMeta } from '@thinkalpha/ui-types/contracts/resource-query';
import type {
    NewWorkspace,
    UpdatedWorkspace,
    Workspace as LocalContractsWorkspace,
} from '@thinkalpha/ui-types/contracts/workspace';
import { isAxiosError } from 'axios';
import { map } from 'rxjs';
import type { Observable } from 'rxjs';
import { processQuery } from 'src/lib/paging';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'api:workspaces' });

interface WorkspaceDimensions {
    columnCount: number;
    rowCount: number;
}

interface MaybeWithDimensions {
    dimensions?: WorkspaceDimensions;
}

export type LocalWorkspaceWithServerMetadata = LocalContractsWorkspace & Pick<Workspace, 'isUserDefault'>;
export type LocalWorkspaceBundle = Omit<WorkspaceBundle, 'workspace'> & {
    workspace: LocalWorkspaceWithServerMetadata;
};

const addDimensionsToWorkspace = (
    workspace: (UpdatedWorkspace | NewWorkspace | Workspace) & MaybeWithDimensions,
): (UpdatedWorkspace | NewWorkspace | Workspace) & MaybeWithDimensions => {
    if (!('dimensions' in workspace)) {
        return {
            ...workspace,
            dimensions: {
                columnCount: 1,
                rowCount: 1,
            },
        };
    } else {
        return workspace;
    }
};

const stripKeyboardCommands = (workspace: Workspace): Workspace => ({
    ...workspace,
    containers: workspace.containers.map((widgetContainer) => ({
        ...widgetContainer,
        tabs: widgetContainer.tabs.map((tab) => {
            const widget: Widget = { ...tab.widget };
            delete widget.keyboardCommands;
            return { ...tab, widget };
        }),
    })),
});

export const mapToCurrentUIExpectations = (workspace: Workspace): LocalWorkspaceWithServerMetadata => {
    const normalizedWorkspace = stripKeyboardCommands(workspace);
    const { dimensions, ...rest } = normalizedWorkspace;

    if (dimensions && (dimensions.columnCount !== 1 || dimensions?.rowCount !== 1)) {
        rest.containers = rest.containers.map((container) => {
            const { layout, ...rest } = container;
            return {
                ...rest,
                layout: {
                    ...layout,
                    h: layout.h / dimensions.rowCount,
                    w: layout.w / dimensions.columnCount,
                    x: layout.x / dimensions.columnCount,
                    y: layout.y / dimensions.rowCount,
                },
            };
        });
    }

    // @ts-expect-error - The local contract types are all out of wack
    // We could try to map the types but I'm concerned that the app currently _works_ using the local contract types
    // and modifying responses to adhere to those types could have consequences.
    return rest;
};

export async function getDefaultWorkspace(expandImports?: true): Promise<LocalContractsWorkspace | null> {
    const response = await api.get<Workspace>(`/workspaces/default${expandImports ? '?$expand=imports' : ''}`, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    return response.status === 404 ? null : mapToCurrentUIExpectations(response.data);
}

export async function getWorkspaceById(id: string): Promise<LocalContractsWorkspace> {
    const workspace = await api
        .get<Workspace>(`/workspaces/${id}?$expand=imports`)
        .then((res) => mapToCurrentUIExpectations(res.data));

    return workspace;
}

export async function getWorkspaceBundle(id: 'default' | string): Promise<LocalWorkspaceBundle> {
    const bundle = await api.get<WorkspaceBundle>(`/workspaces/${id}/bundle`).then((res) => res.data);

    return {
        ...bundle,
        workspace: mapToCurrentUIExpectations(bundle.workspace),
    };
}

/**
 * This is an ugly hack. Cover your eyes.
 */
export function mapWatchlistDisplayModeToWidgetType(workspace: LocalContractsWorkspace): LocalContractsWorkspace {
    for (const container of workspace.containers) {
        for (const tab of container.tabs) {
            if (tab.widget.type === 'results' && (tab.widget as any).displayMode === 'watchlist') {
                (tab.widget as any).type = 'watchlist';
            }
        }
    }
    return workspace;
}

export async function createWorkspace(workspace: NewWorkspace): Promise<LocalContractsWorkspace> {
    const res = await api.post<Workspace>(`/workspaces?$expand=imports`, addDimensionsToWorkspace(workspace));
    return mapToCurrentUIExpectations(res.data);
}

export async function saveWorkspace(
    workspace: UpdatedWorkspace,
    expandImports?: true,
): Promise<LocalContractsWorkspace> {
    const payload = {
        ...addDimensionsToWorkspace(workspace),
        channels: [],
    };
    try {
        const res = await api.put<Workspace>(
            `/workspaces/${workspace.id}${expandImports ? '?$expand=imports' : ''}`,
            payload,
        );
        return mapToCurrentUIExpectations(res.data);
    } catch (error) {
        if (isAxiosError(error)) {
            log.error('Failed to save workspace', {
                workspaceId: workspace.id,
                payload,
                responseStatus: error.response?.status,
                responseData: error.response?.data,
            });
        } else {
            log.error('Failed to save workspace', {
                workspaceId: workspace.id,
                payload,
                error,
            });
        }
        throw error;
    }
}

export function deleteWorkspace(id: string): Observable<void> {
    return rxapi.delete(`/workspaces/${id}`).pipe(
        map(() => {
            /* void */
        }),
    );
}

export function getWorkspacesQueryP(query: ResourceQuery): Promise<ResourceQueryResponseWithMeta<WorkspaceRecord>> {
    return api.get(`/workspaces/query${processQuery({ ...query, includeMeta: true })}`).then((x) => x.data);
}

export function getWorkspacesQuery(query: ResourceQuery): Observable<ResourceQueryResponseWithMeta<WorkspaceRecord>> {
    return rxapi.get(`/workspaces/query${processQuery({ ...query, includeMeta: true })}`).pipe(map((x) => x.data));
}
export function getWorkspacesCountQuery(): Observable<ResourceQueryResponseWithMeta<WorkspaceRecord>> {
    return rxapi.get(`/workspaces/query?$select=&$count=true`).pipe(map((x) => x.data));
}

export function getWorkspaceConfig(): Promise<WorkspaceConfig> {
    return api.get('/workspaces/config').then((res) => res.data);
}

export async function getContainerTemplates(expandImports?: true): Promise<LocalContractsWorkspace[]> {
    const res = await api.get<Workspace[]>(`/workspaces/templates${expandImports ? '?$expand=imports' : ''}`);
    return res.data.map(mapToCurrentUIExpectations).map(mapWatchlistDisplayModeToWidgetType);
}
